/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-unused-vars */
import './style.scss';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';

import { classNames } from 'primereact/utils';
import { getAllWithNoFilter as getAllProposalPrograms } from 'apis/proposalPrograms.api';
import {
  getAllProposal,
  approveProposal,
  downloadZip,
  updateProposalStatus,
  deleteProposal,
} from 'apis/proposals.api';

import ConfirmDialog from 'features/Proposal/components/ConfirmDialog';
import SearchFilters from 'features/Proposal/components/SearchFilters';

import ErrorDialog from 'components/ErrorDialog';
import Pagination from 'components/Pagination';
import { PROPOSAL_STATUSES } from 'constant';
import RequestUpdateForm from 'features/ReviseProjectProposal/components/RequestUpdateForm';
import CreateContractForm from 'features/ReviseProjectProposal/components/CreateContractForm';
import { mapError } from 'utils/func';
import { totalExpenditureSchema } from 'features/ReviseProjectProposal/validation';
import useSelectionProposalManagement from 'utils/hooks/useSelectionProposalManagement';
import { AbilityContext, Can } from 'components/Can';
import ViewHistoryProposal from 'features/Proposal/components/ViewHistoryProposal';
import ProgramDialog from 'features/Proposal/components/ProgramDialog';
import { getAllOrganizations } from 'apis/projectProposals.api';

function ProposalViewList() {
  // #region data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const dialogConfirmRef = useRef();
  const dialogRequestUpdateRef = useRef();
  const dialogCreateContractRef = useRef();
  const dialogConfirmDeleteRef = useRef();
  const approvalMenu = useRef(null);
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [viewHistoryVisible, setViewHistoryVisible] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  const [isVisibleProgramDialog, setIsVisibleProgramDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const ability = useContext(AbilityContext);

  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _ownerName: { value: null },
    _proposalProgramIds: { value: null },
    _organizationIds: { value: null },
    _yearProposed: { value: null },
    _status: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: { value: null },
    ownerName: { value: null },
    proposalProgramIds: { value: null },
    organizationIds: { value: null },
    yearProposed: { value: null },
    status: { value: null },
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: { value: null },
    ownerName: { value: null },
    proposalProgramIds: { value: null },
    organizationIds: { value: null },
    yearProposed: { value: null },
    status: { value: null },
  });

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['proposal', pagination, appliedFilters],
    ({ queryKey: [, _pagination, _filters] }) => getAllProposal(_pagination, _filters),
    {
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const { data: proposalProgramData } = useQuery(['proposalPrograms'], () =>
    getAllProposalPrograms()
  );
  const programs = useMemo(() => proposalProgramData?.data?.data ?? [], [proposalProgramData]);

  const { data: organizationResponse } = useQuery(['organizationResponse'], () =>
    getAllOrganizations()
  );

  const organizations = useMemo(() => organizationResponse?.data ?? [], [organizationResponse]);

  const typeProposalOptions = [
    { name: t('proposal.typeProposal.project'), value: 'project' },
    { name: t('proposal.typeProposal.topic'), value: 'topic' },
    { name: t('proposal.typeProposal.productionProject'), value: 'production_project' },
  ];

  const statuses = [
    {
      id: '30ce149a-6db3-4e04-8e8e-7111761d874f',
      code: 'new',
      name: 'new',
    },
    {
      id: '30ce149a-6db3-4e04-8e8e-7111761d874f',
      code: 'submitted',
      name: 'submitted',
    },
    {
      id: '6b95e360-5cb5-42f4-b1ee-0ed9d0ae4033',
      code: 'approved',
      name: 'approved',
    },
    {
      id: '6b95e360-5cb5-42f4-b1ee-0ed9d0ae4933',
      code: 'rejected',
      name: 'rejected',
    },
  ].map((status) => ({
    id: status.id,
    code: status?.name,
    name: status?.name ? t(`statuses.${status.name}`) : '',
  }));

  const yearProposed = [
    {
      id: '30ce149a-6db3-4e04-8e8e-7111761d874a',
      value: '2025',
    },
    {
      id: '6b95e360-5cb5-42f4-b1ee-0ed9d0ae403b',
      value: '2024',
    },
    {
      id: '897192e0-7910-4d98-a88f-f0f0128f9103',
      value: '2023',
    },
    {
      id: '897192e0-7910-4d98-a88f-f0f0128f9104',
      value: '2022',
    },
    {
      id: '897192e0-7910-4d98-a88f-f0f0128f9105',
      value: '2021',
    },
    {
      id: '897192e0-7910-4d98-a88f-f0f0128f9106',
      value: '2020',
    },
  ];

  const { mutate: mutateDowloadZip, isLoading: isDowloadZipLoading } = useMutation(downloadZip);

  const {
    mutate: mutateDownloadZip,
    isLoading: isDownloadZipLoading,
    isError: isDownloadZipError,
    error: downloadZipError,
  } = useMutation(downloadZip);

  const {
    selectedItems,
    unselectedItems,
    isSelectedAll,
    isUnselectedSome,
    handleAllRowSelect,
    handleAllRowUnselect,
    handleRowSelect,
    handleRowUnselect,
  } = useSelectionProposalManagement(data, pagination);

  const handleDownloadZip = () => {
    // Call api download files
    // param:selectedItems,unselectedItems,isSelectedAll,isUnselectedSome,researchFieldId,statusName
    mutateDownloadZip(
      {
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const contentDisposition = res?.headers['content-disposition'];
            const filenameMatch = contentDisposition?.match(/filename='(.+)'/);
            const filename = filenameMatch ? filenameMatch[1] : 'Danh sach de xuat DHQG.zip';
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;

            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };
  const approvalItems = [
    {
      label: t('projectProposal.viewList.approve'),
      icon: 'pi pi-check',
      command: () => {},
    },
    { separator: true },
    {
      label: t('projectProposal.viewList.changeRequest'),
      icon: 'pi pi-refresh',
      command: () => {},
    },
  ];

  const { mutate: mutateUpdateStatus } = useMutation(updateProposalStatus);
  const { mutate: mutateDelete } = useMutation(deleteProposal);

  // #endregion Data

  // #region Event
  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    setFilters({
      ...localFilters,
    });
    setAppliedFilters({
      ...localFilters,
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
    setIsLoadedFilterFromLocalStorage(true);
  }, []);

  // #endregion Event

  const handleChangeProgram = (program) => {
    setSelectedProgram(program);
  };

  const handleDowloadZip = () => {
    // Call api export excel
    // param:selectedItems,unselectedItems,isSelectedAll,isUnselectedSome,researchFieldId,statusName
    mutateDowloadZip(
      {
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const contentDisposition = res?.headers['content-disposition'];
            const filenameMatch = contentDisposition?.match(/filename='(.+)'/);
            const filename = filenameMatch ? filenameMatch[1] : 'Danh sach de xuat.zip';
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;

            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  // #region Render
  const isRowSelectable = (event) => event.data.deletedAt === null;

  const formatName = (value) => {
    if (value?.deletedAt === null) {
      return (
        <>
          <Can do="read" on="proposal">
            <Link to={`/proposals/${value?.id}`} className="hover:underline">
              {value?.vietnameseName}
            </Link>
          </Can>
          <Can not do="read" on="proposal">
            <span>{value?.vietnameseName}</span>;
          </Can>
        </>
      );
    }

    return <span>{value?.vietnameseName}</span>;
  };

  // Filter name
  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('proposalList.vietnameseName')}
      className="p-column-filter"
    />
  );

  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('proposalList.ownerName')}
      className="p-column-filter"
    />
  );

  const programFilter = () => (
    <MultiSelect
      value={filters.proposalProgramIds}
      onChange={(e) => {
        const newFilters = { ...filters, proposalProgramIds: e.value };
        setFilters(newFilters);
      }}
      options={programs}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.program')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
      panelStyle={{ maxWidth: '680px' }}
    />
  );

  const organizationFilter = () => (
    <MultiSelect
      value={filters.organizationIds}
      onChange={(e) => {
        const newFilters = { ...filters, organizationIds: e.value };
        setFilters(newFilters);
      }}
      options={organizations}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.organization')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
      panelStyle={{ maxWidth: '680px' }}
    />
  );

  const yearProposedFilter = () => (
    <MultiSelect
      value={filters.yearProposed}
      onChange={(e) => {
        const newFilters = { ...filters, yearProposed: e.value };
        setFilters(newFilters);
      }}
      options={yearProposed}
      optionLabel="value"
      optionValue="value"
      filter
      showClear
      placeholder={t('proposalList.yearProposed')}
      style={{ maxWidth: '16rem', width: '12rem' }}
      panelStyle={{ maxWidth: '240px' }}
    />
  );

  const statusFilter = () => (
    <MultiSelect
      value={filters.status}
      onChange={(e) => {
        const newFilters = { ...filters, status: e.value };
        setFilters(newFilters);
      }}
      options={statuses}
      optionLabel="name"
      optionValue="code"
      filter
      showClear
      placeholder={t('projectProposal.viewList.status')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
      panelStyle={{ maxWidth: '680px' }}
    />
  );

  const formatProposalType = (value) =>
    typeProposalOptions.map(
      (type) => type.value === value?.type && <p className="text-center m-0">{type.name}</p>
    );

  const formatResearchFields = (value) => (
    <div>
      {value?.proposalResearchFields
        ?.sort((a, b) => {
          if (a.priority === null) return 1;
          if (b.priority === null) return -1;
          if (a.priority === b) return 0;
          return a.priority < b.priority ? -1 : 1;
        })
        ?.map((researchField) => (
          <p key={researchField.researchField.id} className="text-left m-0">
            {`${t(`researchFields.${researchField.researchField.code}`)}`}
          </p>
        ))}
    </div>
  );

  const formatStatus = (value) => {
    const statusColorMapping = {
      new: 'success',
      submitted: 'info',
      rejected: 'danger',
    };

    const { status } = value;

    return (
      <Tag
        style={{ maxWidth: '8rem' }}
        severity={statusColorMapping[status]}
        value={t(`statuses.${status}`)}
        rounded
      />
    );
  };

  const formatActions = (value) => {
    const menuRef = useRef(null);

    const items = [];

    // Edit (chỉ khi status là 'new')
    if (value?.status === 'new' && ability.can('update', 'proposal')) {
      items.push({
        template: () => (
          <Link to={`/proposals/edit/${value.id}`} className="p-menuitem-link">
            <span className="p-menuitem-text">{t('proposal.edit.tooltip')}</span>
          </Link>
        ),
      });
    }

    // Xem lịch sử trạng thái của đề tài
    if (ability.can('read', 'proposal')) {
      items.push({
        label: t('proposalList.viewList.tooltip.viewHistory'),
        command: () => {
          setProjectHistory(value?.proposalLogs ?? []);
          setViewHistoryVisible(true);
        },
      });
    }

    // Update status (chỉ khi status khác 'new')
    if (value?.status !== 'new' && ability.can('update', 'proposal')) {
      items.push({
        label: t('proposalList.viewList.updateStatus'),
        command: () => {
          dialogConfirmRef.current.open({
            mode: 'updateStatus',
            mutate: mutateUpdateStatus,
            value,
          });
        },
      });
    }

    // Thay đổi chương trình
    if (ability.can('update', 'proposal')) {
      items.push({
        label: t('projectProposal.viewList.changeProgram'),
        command: () => {
          setIsVisibleProgramDialog(true);
          setSelectedProgram({
            ...value.proposalProgram,
            proposalId: value.id,
            vietnameseName: value.vietnameseName,
            englishName: value.vietnameseName,
          });
        },
      });
    }

    // Xóa
    items.push({
      label: t('proposal.delete.label'),
      command: () => {
        dialogConfirmRef.current.open({
          mode: 'delete',
          mutate: mutateDelete,
          value,
        });
      },
    });

    return (
      <div className="flex justify-content-center">
        <Menu model={items} popup ref={menuRef} style={{ minWidth: '250px' }} />
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Button
            icon="pi pi-ellipsis-v"
            className="p-button-rounded p-button-text"
            onClick={(event) => menuRef.current.toggle(event)}
          />
        </div>
      </div>
    );
  };

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      disabled={
        !totalExpenditureSchema.isValidSync({
          min: filters?.totalExpenditure?.min ?? undefined,
          max: filters?.totalExpenditure?.max ?? undefined,
        })
      }
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const renderHeader = () => (
    <div className="flex justify-content-between">
      <Button
        type="button"
        icon="pi pi-filter"
        label={t('projectProposal.viewList.searchFilters')}
        outlined
        onClick={() => setIsVisibleSearchFilters(true)}
      />
    </div>
  );

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, error)}</h4>;
  }

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex align-items-center justify-content-between gap-2 pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">{t('proposalList.title')}</h4>
        <div className="flex gap-2">
          <Button
            className="bg-cyan-600 border-cyan-600"
            label={t('proposal.cerateProposal')}
            icon="pi pi-plus"
            onClick={() => navigate('/proposals/create')}
          />

          <Can do="read" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('proposalList.viewList.downloadZip')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isDownloadZipLoading },
                  { 'pi pi-download': !isDownloadZipLoading }
                )}
                severity="info"
                onClick={handleDownloadZip}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isDownloadZipLoading
                }
              />
            </div>
          </Can>
        </div>
      </div>

      {!isLoading && data?.data?.length ? (
        <Checkbox
          style={{ position: 'absolute', top: '180px', left: '43px', zIndex: 10 }}
          checked={isSelectedAll}
          onChange={() => (!isSelectedAll ? handleAllRowSelect() : handleAllRowUnselect())}
        />
      ) : null}

      <DataTable
        value={data?.data}
        className="proposal-list"
        dataKey="id"
        header={renderHeader}
        showGridlines
        scrollable
        selection={selectedItems}
        selectionMode="multiple"
        showSelectAll={false}
        onRowSelect={handleRowSelect}
        onRowUnselect={handleRowUnselect}
        isDataSelectable={isRowSelectable}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: '3rem' }}
          frozen
          alignFrozen="center"
        />
        <Column
          header={t('proposalList.vietnameseName')}
          headerStyle={filterActiveStyle('vietnameseName')}
          field="vietnameseName"
          body={formatName}
          filter
          filterField="_vietnameseName"
          filterElement={vietnameseNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, vietnameseName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', maxWidth: '16rem', textAlign: 'left', color: '#6366F1' }}
          frozen
          alignFrozen="center"
        />
        <Column
          header={t('proposalList.proposalType')}
          body={formatProposalType}
          style={{ minWidth: '8rem', maxWidth: '16rem', textAlign: 'center', color: '#6366F1' }}
        />
        <Column
          header={t('proposalList.projectType')}
          field="projectType"
          style={{ minWidth: '8rem', maxWidth: '16rem', textAlign: 'center', color: '#6366F1' }}
        />
        <Column
          header={t('proposalList.ownerName')}
          headerStyle={filterActiveStyle('ownerName')}
          field="owner.fullname"
          filter
          filterField="_ownerName"
          filterElement={ownerNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, ownerName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', maxWidth: '16rem', textAlign: 'center', color: '#6366F1' }}
        />
        <Column
          field="owner.organization.name"
          header={t('proposalList.organization')}
          headerStyle={filterActiveStyle('proposalProgramIds')}
          filter
          filterField="_organizationIds"
          filterElement={organizationFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, organizationIds: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          field="proposalProgram.name"
          header={t('proposalList.program')}
          headerStyle={filterActiveStyle('proposalProgramIds')}
          filter
          filterField="_proposalProgramIds"
          filterElement={programFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, proposalProgramIds: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          header={t('proposalList.researchField')}
          body={formatResearchFields}
          style={{ minWidth: '10rem', textAlign: 'center' }}
        />
        <Column
          field="yearProposed"
          header={t('proposalList.yearProposed')}
          headerStyle={filterActiveStyle('yearProposed')}
          filter
          filterField="_yearProposed"
          filterElement={yearProposedFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, yearProposed: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem', textAlign: 'center' }}
        />
        <Column
          header={t('proposalList.status')}
          headerStyle={filterActiveStyle('status')}
          body={formatStatus}
          filter
          filterField="_status"
          filterElement={statusFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              status: [],
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem', textAlign: 'center' }}
        />
        <Column
          header={t('proposalList.action')}
          headerStyle={filterActiveStyle('reviewDeadline')}
          body={formatActions}
          style={{ minWidth: '6rem', textAlign: 'center' }}
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        pageName="councils"
      />

      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          programs={programs}
          organizations={organizations}
          statuses={statuses}
          yearProposed={yearProposed}
        />
      )}

      <Menu model={approvalItems} popup ref={approvalMenu} />

      <ViewHistoryProposal
        data={projectHistory}
        visible={viewHistoryVisible}
        setVisible={setViewHistoryVisible}
      />

      <ConfirmDialog ref={dialogConfirmRef} refetch={refetch} />
      <RequestUpdateForm ref={dialogRequestUpdateRef} />

      <CreateContractForm ref={dialogCreateContractRef} />

      <ProgramDialog
        isVisible={isVisibleProgramDialog}
        setIsVisible={setIsVisibleProgramDialog}
        currentData={selectedProgram}
        onSelectedChange={handleChangeProgram}
        refetch={refetch}
      />

      <ErrorDialog
        title={t('proposalList.downloadZipErrorTitle')}
        isError={isDownloadZipError}
        error={downloadZipError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
    </div>
  );
  // #endregion Render
}

export default ProposalViewList;
